/**
 * advance-ai.ts — per-tick AI opponents pass (M9 capstone).
 *
 * Runs after the other advance passes so the AI sees fully-updated state
 * each decision tick: resources already folded, research ticked, ships
 * spawned / despawned, structures promoted, combat resolved.
 *
 * Rate-limited to AI_TICK_INTERVAL: each non-player empire takes one
 * action per AI-tick (one structure build OR one ship queue OR one
 * research set OR one Constructor build) plus movement orders for all
 * idle ships. Keeps AI pacing visually calm and per-tick cost bounded.
 *
 * Per-race personalities come from `balance-ai.ts`:
 *   - RACE-AA Terran Consortium — turtle (economy / defence-first)
 *   - RACE-BB Kargath Imperium — rush (military-first, aggressive)
 *   - RACE-CC Reman Trust — hit-and-run (stealth, cloak-by-default,
 *     aggressive-at-range)
 *
 * All decisions flow through `applyCommand` so AI actions go through the
 * same validation + cost-deduction path as player actions. Determinism
 * is preserved — this pass reads state + writes through applyCommand,
 * no extra RNG draws.
 */

import { applyCommand } from './commands';
import {
  getAiConfig,
  type AiRaceConfig,
} from './balance-ai';
import { COLONISER_SPECIAL_ABILITY } from './balance-colonisation';
import { BIOME_XLSX_LABEL } from './balance-habitability';
import {
  CONSTRUCTOR_CHASSIS_BY_RACE,
  STATIONARY_SPECIAL_ABILITY,
  spaceBuildCostForKind,
  spaceSiteRequirementForKind,
} from './balance-space-structures';
import {
  buildCreditsForKind,
  buildMineralsForKind,
  isUniqueKind,
  maxLevelForKindByRace,
  maxStructuresForSize,
} from './balance-structures';
import { canBuildClassAtPlanet } from './planet-economy';
import {
  effectiveMaxHull,
  hyperdriveCostMultiplier,
  hyperdriveRangeMultiplier,
  hyperdriveSpeedMultiplier,
} from './research-effects';
import { firstStepTowards } from './pathfinding';
import { pickCombatJumpDestinationThreatWeighted } from './advance-ai-targeting';
import { decideShipCloakAction } from './advance-ai-cloaking';
import { detectHomeDefenceThreat } from './advance-ai-home-defence';
import { pairScoreForGateCandidate, pickBestGateCandidatePair } from './advance-ai-gates';
import { pushTeamClaim } from './advance-ai-team-claims';
import { resolveStandingOrderForShip } from './advance-ai-standing-orders';
import {
  warpGateConnectionCostFuel,
  warpGateConnectionCostMinerals,
} from './balance-space-structures';
import { CLOAKING_TECH_ID } from './balance-detection';
import { WARP_GATE_CHASSIS } from './balance-space-structures';
import {
  chooseDiplomaticTarget,
  decideOutcome,
  effectiveDiplomaticAggression,
  PICK_EPSILON,
} from './advance-ai-diplomacy';
import { diplomaticPerTickForEmpire } from './planet-economy';
import { nextFloat } from '../state/rng';
import type {
  ArchetypeRow,
  CloakingRuleRow,
  DifficultyModifierRow,
  EffectiveBehaviour,
  Empire,
  EmpireId,
  EnvironmentEntity,
  GameState,
  Planet,
  RaceOverlayRow,
  ShipInstance,
  StandingOrdersRow,
  StructureKind,
  SystemId,
} from '../state/types/index';
import { EMPIRE_NEUTRAL_ID } from '../state/types/index';

// ---------------------------------------------------------------------------
// v4M8.1b — composeEffectiveBehaviour
// ---------------------------------------------------------------------------

/**
 * Fallback archetype row used when an empire's `aiArchetype` doesn't match
 * any row in `state.aiBehaviours.archetypes`. Values picked to be
 * conservative (no aggressive bias) so a misconfigured empire defaults to
 * a reasonable defender-ish profile rather than running off the rails.
 */
const FALLBACK_ARCHETYPE: ArchetypeRow = {
  name: 'Fallback',
  minTaskForceSize: 4,
  minReadinessRatio: 1.2,
  strategicWeight: 1.0,
  dangerFactor: 1.0,
  pathFuelWeight: 1.0,
  recallFraction: 0.5,
  warningRecallSize: 1,
  archetypeGateAffinity: 1.0,
  claimTtlTicks: 200,
  teammatePenalty: 0.3,
  diplomaticTeammatePenalty: 0.2,
  diplomaticClaimDecayTicks: 1000,
  diplomaticActionPriority: 'Trade',
  researchPriority: 0.6,
  economicStructureBias: 1.0,
};

/**
 * Fallback difficulty modifier row used when an empire's `aiDifficulty`
 * row is absent. Identity multipliers so the archetype's nominal values
 * flow through unchanged.
 */
const FALLBACK_DIFFICULTY: DifficultyModifierRow = {
  name: 'Fallback',
  aggressionMul: 1.0,
  dispatchMul: 1.0,
  riskMul: 1.0,
  aiTickRate: 1.0,
  coordinatedAssaultEnabled: false,
  preEmptiveRecallEnabled: false,
  strategicGateBuildingEnabled: false,
  raceAdvancedStrategyEnabled: false,
};

/**
 * Fallback race overlay used when an empire's `raceId` has no row in
 * `state.aiBehaviours.raceOverlays`. Identity adjustments + empty
 * race-specific strings so the archetype + difficulty layers stand alone.
 */
const FALLBACK_RACE_OVERLAY: RaceOverlayRow = {
  name: 'Fallback',
  archetypeBiasAdjustments: '',
  cloakingMode: 'default',
  gateAffinityMul: 1.0,
  diplomaticPreference: '',
  economicStructureBiasMul: 1.0,
  combatRiskToleranceMul: 1.0,
  advancedStrategyAtBrutal: '',
};

/**
 * Fallback standing-orders row matching the AI.md §5.10 default column
 * vocabulary. Used when no row exists for the empire's archetype id.
 */
const FALLBACK_STANDING_ORDERS: StandingOrdersRow = {
  scoutDefault: 'explore',
  combatSmallDefault: 'idle',
  combatCapitalDefault: 'guard-homeworld',
  constructorDefault: 'idle',
  coloniserDefault: 'idle',
};

/**
 * Fallback cloaking rule (AI.md §5.4 generic default). Used when no row
 * exists for the empire's archetype id.
 */
const FALLBACK_CLOAKING_RULE: CloakingRuleRow = {
  constructorPreBuildUncloakLeadTicks: 3,
  combatInForeignCloak: true,
  combatInOwnCloak: false,
  combatEngageUncloak: true,
  scoutInForeignCloak: true,
};

/**
 * v4M8.1b — compose the empire's effective AI behaviour from the three
 * xlsx-authored layers (archetype + difficulty modifier + race overlay)
 * per `docs/AI.md §1`.
 *
 * Numeric composition follows the AI.md §3-§4 multiplier chain:
 *   - `strategicWeight` × `aggressionMul`
 *   - `dangerFactor` × `riskMul`
 *   - `minTaskForceSize` × `dispatchMul` (ceil-ed back to int)
 *   - `minReadinessRatio` × `riskMul` × `race.combatRiskToleranceMul`
 *   - `archetypeGateAffinity` × `race.gateAffinityMul`
 *   - `economicStructureBias` × `race.economicStructureBiasMul`
 *   - `aiTickRate` = difficulty value (no archetype baseline; archetypes
 *     don't ship their own tick-rate column — that lives on difficulty).
 *
 * String overrides:
 *   - `diplomaticActionPriority` — race overlay's `diplomaticPreference`
 *     wins when non-empty; otherwise the archetype's ordering stands.
 *
 * Cloaking composition:
 *   - Start with the archetype's cloaking rule row.
 *   - If `empire.raceId === 'RACE-CC'` AND a row keyed `'RACE-CC'` exists
 *     in the cloaking-rules catalogue, that row's `combatEngageUncloak`
 *     overrides the archetype's (AI.md §5.4 Phantom Advanced Cloaking).
 *
 * v4M8.1b ships this helper as additive — the live dispatch path in
 * `takeEconomyAction` etc. still reads `getAiConfig(empire, …)`. v4M8.2+
 * migrates consumers to consult the EffectiveBehaviour record instead.
 *
 * Pure — no state mutation, no RNG draws. Safe to call any number of
 * times per tick.
 */
export function composeEffectiveBehaviour(
  state: GameState,
  empire: Empire,
): EffectiveBehaviour {
  const archetypeId = empire.aiArchetype;
  const difficultyId = empire.aiDifficulty;
  const raceOverlayId = empire.raceId;

  const archetype =
    state.aiBehaviours.archetypes[archetypeId] ?? FALLBACK_ARCHETYPE;
  const difficulty =
    state.aiBehaviours.difficultyModifiers[difficultyId] ??
    FALLBACK_DIFFICULTY;
  const race =
    state.aiBehaviours.raceOverlays[raceOverlayId] ?? FALLBACK_RACE_OVERLAY;

  const standingOrders =
    state.aiBehaviours.standingOrders[archetypeId] ?? FALLBACK_STANDING_ORDERS;

  // Cloaking: archetype row, with RACE-CC override applied only to
  // `combatEngageUncloak` when the empire is Phantom (per AI.md §5.4).
  const archetypeCloak =
    state.aiBehaviours.cloakingRules[archetypeId] ?? FALLBACK_CLOAKING_RULE;
  const phantomOverride =
    empire.raceId === 'RACE-CC'
      ? state.aiBehaviours.cloakingRules['RACE-CC']
      : undefined;
  const cloakingRule: CloakingRuleRow = phantomOverride
    ? { ...archetypeCloak, combatEngageUncloak: phantomOverride.combatEngageUncloak }
    : archetypeCloak;

  // Numeric composition. See AI.md §3-§4 for the multiplier chain.
  const minTaskForceSize = Math.max(
    1,
    Math.ceil(archetype.minTaskForceSize * difficulty.dispatchMul),
  );
  const minReadinessRatio =
    archetype.minReadinessRatio *
    difficulty.riskMul *
    race.combatRiskToleranceMul;
  const strategicWeight = archetype.strategicWeight * difficulty.aggressionMul;
  const dangerFactor = archetype.dangerFactor * difficulty.riskMul;
  const archetypeGateAffinity =
    archetype.archetypeGateAffinity * race.gateAffinityMul;
  const economicStructureBias =
    archetype.economicStructureBias * race.economicStructureBiasMul;

  // String override: race overlay's diplomatic preference wins when set.
  const diplomaticActionPriority =
    race.diplomaticPreference && race.diplomaticPreference.length > 0
      ? race.diplomaticPreference
      : archetype.diplomaticActionPriority;

  return {
    archetypeId,
    difficultyId,
    raceOverlayId,
    minTaskForceSize,
    minReadinessRatio,
    strategicWeight,
    dangerFactor,
    pathFuelWeight: archetype.pathFuelWeight,
    recallFraction: archetype.recallFraction,
    warningRecallSize: archetype.warningRecallSize,
    archetypeGateAffinity,
    claimTtlTicks: archetype.claimTtlTicks,
    teammatePenalty: archetype.teammatePenalty,
    diplomaticTeammatePenalty: archetype.diplomaticTeammatePenalty,
    diplomaticClaimDecayTicks: archetype.diplomaticClaimDecayTicks,
    diplomaticActionPriority,
    researchPriority: archetype.researchPriority,
    economicStructureBias,
    aiTickRate: difficulty.aiTickRate,
    coordinatedAssaultEnabled: difficulty.coordinatedAssaultEnabled,
    preEmptiveRecallEnabled: difficulty.preEmptiveRecallEnabled,
    strategicGateBuildingEnabled: difficulty.strategicGateBuildingEnabled,
    raceAdvancedStrategyEnabled: difficulty.raceAdvancedStrategyEnabled,
    cloakingMode: race.cloakingMode,
    advancedStrategyAtBrutal: race.advancedStrategyAtBrutal,
    standingOrders,
    cloakingRule,
  };
}

/**
 * Run one AI tick for every non-player empire. Mutates state via commands.
 *
 * v3M7.1 — each empire's tickInterval is per-(difficulty) rather than a
 * single global. Hard-difficulty empires decide twice as often as Normal;
 * Easy half as often. We gate `state.tick % cfg.tickInterval` per empire
 * so different difficulties can coexist in the same game without one
 * suppressing the others.
 */
export function advanceAi(state: GameState): void {
  const empireIds = [...state.empires.keys()].sort((a, b) => a - b);
  for (const empireId of empireIds) {
    const empire = state.empires.get(empireId)!;
    if (empire.isPlayer) continue;
    // v3M21.1 — synthetic neutral empire is AI-inert: no economy actions,
    // no movement dispatch, no research. Filter early so the rest of the
    // pass doesn't have to defend against it.
    if (empire.id === EMPIRE_NEUTRAL_ID) continue;
    const config = getAiConfig(empire, state.aiBehaviours);
    if (config.tickInterval > 0 && state.tick % config.tickInterval !== 0) continue;
    // One economy action per AI-tick (priority-ordered).
    takeEconomyAction(state, empire, config);
    // v3M7.3c — retreat damaged combat ships first; the retreat order
    // overrides any engage / patrol moveOrder so a low-hull ship doesn't
    // sit dying in front of an enemy because it's already mid-engagement.
    executeRetreats(state, empire, config);
    // v3M7.3a — dispatch idle Colonisers before patrol/engage so the
    // moveOrder set by moveAndColonise gets respected by the move pass.
    dispatchIdleColonisers(state, empire, config);
    // v3M7.3b — dispatch idle Scouts on inter-system jumps for
    // exploration. Same ordering rule: setting moveOrder must happen
    // before issueMovementOrders so the patrol/engage pass skips them.
    dispatchScoutJumps(state, empire, config);
    // MFix7.5b — dispatch idle combat ships on inter-system jumps once
    // the empire is either Aggressive OR has at least one defensive
    // structure on its homeworld. Without this pass the AI never
    // projects force across system boundaries, so games on multi-system
    // maps stalemate at the home-system frontier.
    // v4M8.3 — dispatchCombatJumps composes its own EffectiveBehaviour
    // and no longer reads AiRaceConfig. config stays in scope for the
    // other consumers (economy / research / movement orders).
    dispatchCombatJumps(state, empire);
    // v4M8.5b — home-defence recall. Runs AFTER dispatchCombatJumps so
    // a critical-tier threat overrides a forward dispatch that the
    // same tick just issued (the recall re-dispatches affected ships
    // back home via moveAndJumpShips, cancelling their forward jump).
    // Detection + tier resolution in `advance-ai-home-defence.ts`;
    // gates / recall-fraction / warningRecallSize / Brutal pre-emptive
    // flag all flow from the composed EffectiveBehaviour.
    dispatchHomeDefenceRecall(state, empire);
    // v4M8.4b — per-tick cloak decisions for the empire's cloakable ships.
    // Runs AFTER combat dispatch so ships that were just sent into foreign
    // territory pick up the foreign-territory cloak rule on the same tick,
    // and BEFORE constructor dispatch so a Constructor entering the build
    // site already has the right cloak state when its constructionOrder is
    // assigned. Per-ship decisions via `decideShipCloakAction` — own
    // territory / foreign territory / enemy-in-range / Constructor pre-
    // build branches all resolved upstream. See `advance-ai-cloaking.ts`.
    dispatchCloakDecisions(state, empire);
    // MFix7.5a — dispatch idle Constructors to start a space-structure
    // build. Lifted out of the takeEconomyAction cascade so the pass
    // fires every AI-tick rather than only when the planet/ship/research
    // priorities all happen to fail in the same tick (which is rare in
    // early/mid-game and starves the AI of space structures).
    dispatchConstructorBuilds(state, empire, config);
    // v4M8.6b — AI Warp Gate auto-connect heuristic per AI.md §5.6.
    // Runs AFTER `dispatchConstructorBuilds` so the standard space-
    // structure dispatch (primary Constructor task) wins first claim
    // on idle Constructors; gate building is a strategic-overlay
    // task using whichever Constructors remain idle.
    dispatchAiGateBuilds(state, empire);
    // v4M8.6c — Connection follow-up. After gates land in state.ships
    // (built by .6b's `buildSpaceStructure` dispatch), this pass
    // finds the highest-scored pair of UNLINKED live gates and
    // issues a `connectWarpGate` command. Per-tick re-derivation
    // means no extra state-shape change — the dispatch reads the
    // current set of empire gates + warpGateLinks each tick. Same
    // pair-scoring helper as .6a so a gate that landed in a high-
    // value system gets connected first.
    dispatchAiGateConnections(state, empire);
    // v3M21.5.3 — diplomatic focus. AI picks a visible neutral to
    // invest Diplomatic resource in when its effective aggression is
    // above PICK_EPSILON.
    manageDiplomaticFocus(state, empire);
    // v3M21.5.4 — outcome invocation. If focus is set and a threshold
    // is unlocked, decide whether to invoke (or save for Annex if
    // aggressive + reachable in patience window).
    manageDiplomaticInvocation(state, empire);
    // v4M8.8b — apply per-archetype standing orders to ships that
    // no explicit dispatch claimed this tick. Runs LAST in the
    // per-empire loop so combat / cloak / build / recall / gate /
    // diplomatic dispatches all win first claim; only idle leftovers
    // pick up the fallback order. See `applyStandingOrders` for the
    // scope (explore + guard-* + idle handled; patrol-* deferred per
    // §5.10 follow-up because setShipPatrol needs in-system waypoint
    // placement logic that's bigger than this sub-checkpoint).
    applyStandingOrders(state, empire);
    // Movement orders every AI-tick for idle ships.
    issueMovementOrders(state, empire, config);
  }
}

/**
 * v3M21.5.3 — set `empire.diplomaticFocus` if it's null and the
 * empire's effective diplomatic aggression is above `PICK_EPSILON`.
 * Walks visible neutrals via `findVisibleNeutrals`, scores each via
 * `scoreNeutral`, picks the highest score. The per-tick income
 * routing in `advance.ts` step 3a already auto-clears stale focus
 * (e.g. target was annexed last tick), so we only need to handle
 * focus === null here. Issues `setDiplomaticFocus` via `applyCommand`
 * so the validation chain (target is currently a neutral, etc.)
 * runs the same as a player-issued command.
 */
function manageDiplomaticFocus(state: GameState, empire: Empire): void {
  if (empire.diplomaticFocus !== null) return;
  if (effectiveDiplomaticAggression(state, empire) < PICK_EPSILON) return;
  const target = chooseDiplomaticTarget(state, empire);
  if (!target) return;
  applyCommand(state, {
    kind: 'setDiplomaticFocus',
    empireId: empire.id,
    planetId: target.id,
  });
}

/**
 * v3M21.5.4 — invoke a diplomatic outcome on the focus target if one
 * is unlocked and the AI's policy says now is the time. Pure-policy
 * decision delegated to `decideOutcome` (sees the pool, thresholds,
 * federation/trade state, aggression, and per-tick income); this
 * helper only handles the dispatch.
 */
function manageDiplomaticInvocation(state: GameState, empire: Empire): void {
  if (empire.diplomaticFocus === null) return;
  const target = state.planets.get(empire.diplomaticFocus);
  if (!target) return;
  const income = diplomaticPerTickForEmpire(state, empire.id);
  const decision = decideOutcome(state, empire, target, income);
  switch (decision) {
    case 'wait':
      return;
    case 'annex':
      applyCommand(state, {
        kind: 'invokeAnnex',
        empireId: empire.id,
        planetId: target.id,
      });
      return;
    case 'vassal':
      applyCommand(state, {
        kind: 'invokeVassal',
        empireId: empire.id,
        planetId: target.id,
      });
      return;
    case 'federation':
      applyCommand(state, {
        kind: 'invokeFederation',
        empireId: empire.id,
        planetId: target.id,
      });
      return;
    case 'trade':
      applyCommand(state, {
        kind: 'invokeTrade',
        empireId: empire.id,
        planetId: target.id,
      });
      return;
  }
}

// ---------------------------------------------------------------------------
// Economy: one action per AI-tick (priority-ordered)
// ---------------------------------------------------------------------------

/**
 * Walk the race's priority order and fire the first action that resolves.
 * Returns when an action lands or when all priorities exhaust. Priority
 * cascade (roughly): new planet structure → ship build → research →
 * structure upgrade. Constructor space-structure dispatch runs as its
 * own parallel pass (`dispatchConstructorBuilds`, MFix7.5a) so it's not
 * starved by the cascade — early/mid-game the upper priorities almost
 * always succeed, which previously left Constructors idle until very
 * late in the game.
 */
function takeEconomyAction(state: GameState, empire: Empire, config: AiRaceConfig): void {
  if (tryBuildNextStructure(state, empire, config)) return;
  if (tryBuildNextShip(state, empire, config)) return;
  if (tryStartNextResearch(state, empire, config)) return;
  tryUpgradeStructure(state, empire, config);
}

/**
 * Try to build the first structure kind in the race's priority list that
 * can land on one of the empire's planets right now. Uniqueness, slot
 * cap, and resources all delegate to the command applier — AI just
 * picks a candidate and fires buildStructure.
 */
function tryBuildNextStructure(
  state: GameState,
  empire: Empire,
  config: AiRaceConfig,
): boolean {
  for (const kind of config.structurePriority) {
    for (const planetId of empire.planetIds) {
      const planet = state.planets.get(planetId);
      if (!planet) continue;
      if (!canBuildStructureHere(state, planet, empire, kind)) continue;
      applyCommand(state, {
        kind: 'buildStructure',
        empireId: empire.id,
        planetId,
        structureKind: kind,
      });
      return true;
    }
  }
  return false;
}

function canBuildStructureHere(
  state: GameState,
  planet: Planet,
  empire: Empire,
  kind: StructureKind,
): boolean {
  if (planet.ownerEmpireId !== empire.id) return false;
  if (planet.structureIds.length >= maxStructuresForSize(planet.size)) return false;
  const sd = state.structuresData;
  if (isUniqueKind(sd, kind)) {
    for (const id of planet.structureIds) {
      const s = state.structures.get(id);
      if (s && s.kind === kind) return false;
    }
  }
  if (empire.resources.credits < buildCreditsForKind(sd, kind)) return false;
  if (empire.resources.minerals < buildMineralsForKind(sd, kind)) return false;
  return true;
}

/**
 * Try to queue the next priority chassis at a planet with a capable
 * Shipyard. Walks the race's ship priority list, then the empire's
 * planets; on the first resolvable (shipyard class + resources + pop +
 * chassis tech gate) it fires buildShip.
 */
function tryBuildNextShip(
  state: GameState,
  empire: Empire,
  config: AiRaceConfig,
): boolean {
  // MFix6.1 — class-progression heuristic. The legacy implementation
  // walked the priority list and built the FIRST resolvable chassis,
  // which means a list with Coloniser / Engineer / Scout / Corvette at
  // the top causes the AI to spam Class 1 chassis forever once those
  // are affordable — even after Class 4-6 chassis become researched
  // and buildable. The fix: walk the whole priority list, track every
  // resolvable (chassis, planet) pair, and pick the one with the
  // highest classLevel. Priority-list order remains the tie-breaker so
  // role preference (a Cruiser before a Battleship of equal class)
  // still matters.
  let best: {
    chassis: string;
    planetId: number;
    classLevel: number;
    priority: number;
  } | null = null;

  for (let i = 0; i < config.shipPriority.length; i++) {
    const chassis = config.shipPriority[i]!;
    const tmpl = state.shipTemplates.get(`${empire.raceId}:${chassis}`);
    if (!tmpl) continue;
    // Chassis tech gate (M12.3) — Class 1 implicit, Class 2+ needs the tech.
    if (tmpl.classLevel >= 2) {
      const techName = `Class ${tmpl.classLevel} Starship Chassis`;
      if (!empire.researchedTechs.has(techName)) continue;
    }
    if (empire.resources.credits < tmpl.creditsBuildCost) continue;
    if (empire.resources.minerals < tmpl.mineralsBuildCost) continue;
    // Find the first capable planet for this chassis. Lowest planet id
    // wins for determinism; details of which planet are secondary to the
    // chassis selection itself.
    let chosenPlanetId: number | null = null;
    for (const planetId of empire.planetIds) {
      const planet = state.planets.get(planetId);
      if (!planet) continue;
      if (planet.currentPopulation < tmpl.popBuildCost) continue;
      if (!canBuildClassAtPlanet(state, planetId, tmpl.classLevel)) continue;
      chosenPlanetId = planetId;
      break;
    }
    if (chosenPlanetId === null) continue;

    if (
      best === null ||
      tmpl.classLevel > best.classLevel ||
      (tmpl.classLevel === best.classLevel && i < best.priority)
    ) {
      best = {
        chassis,
        planetId: chosenPlanetId,
        classLevel: tmpl.classLevel,
        priority: i,
      };
    }
  }

  if (best === null) return false;
  applyCommand(state, {
    kind: 'buildShip',
    empireId: empire.id,
    planetId: best.planetId,
    chassis: best.chassis,
  });
  return true;
}

/**
 * If no active research, pick the first category in the race's priority
 * list with a researchable tech (all prereqs met, not already done,
 * race-gated correctly) and set it as the target.
 */
function tryStartNextResearch(
  state: GameState,
  empire: Empire,
  config: AiRaceConfig,
): boolean {
  if (empire.activeResearch !== null) return false;

  for (const category of config.researchCategoryPriority) {
    // Within a category, walk techs by ascending level so the AI fills
    // out the bottom of the ladder before reaching higher-cost rungs.
    // v4M5.1 — when the legacy category is missing from the live tech
    // tree (e.g. `'Hyperdrive Fuel Consumption'` was renamed to
    // `'Engines'` at v4M5.1), fall back to a tech-name-prefix match
    // derived from the legacy category. Keeps AI_Behaviours.xlsx stable
    // through the xlsx-side category rename without drifting AI
    // priorities.
    let pool = [...state.techs.values()].filter((t) => t.category === category);
    if (pool.length === 0) {
      const fallbackPrefix = LEGACY_CATEGORY_TO_NAME_PREFIX[category];
      if (fallbackPrefix !== undefined) {
        pool = [...state.techs.values()].filter((t) =>
          t.techName.startsWith(fallbackPrefix),
        );
      }
    }
    const candidates = pool
      .filter((t) => t.race === 'All' || t.race === empire.raceId)
      .filter((t) => !empire.researchedTechs.has(t.techName))
      .filter((t) =>
        t.prerequisites.every((p) => empire.researchedTechs.has(p)),
      )
      .sort((a, b) => {
        const al = a.level ?? Number.POSITIVE_INFINITY;
        const bl = b.level ?? Number.POSITIVE_INFINITY;
        return al - bl;
      });
    if (candidates.length === 0) continue;
    applyCommand(state, {
      kind: 'setResearchTarget',
      empireId: empire.id,
      techName: candidates[0]!.techName,
    });
    return true;
  }
  return false;
}

/**
 * v4M5.1 — back-compat map for AI research-priority category drift.
 *
 * The xlsx authoring side moved hyperdrive techs from the legacy
 * 'Hyperdrive Fuel Consumption' category into the unified 'Engines'
 * category (which now also houses Hyperdrive Speed + Range). AI
 * priority lists in `AI_Behaviours.xlsx` weren't updated at the same
 * time (re-authoring would lump Speed / Range / Efficiency together,
 * losing the fuel-cost specificity); this map lets the dispatch
 * filter techs by tech-name prefix when the category lookup misses.
 *
 * Add entries here when other categories rename in the xlsx. Each
 * entry's prefix should be canonical (i.e. matches the tech-name
 * format the live xlsx authors use).
 */
const LEGACY_CATEGORY_TO_NAME_PREFIX: Record<string, string> = {
  'Hyperdrive Fuel Consumption': 'Hyperdrive Efficiency',
};

/**
 * MFix7.5a — Dispatch idle Constructors to start a space-structure
 * build. Walks each idle Constructor (no current build + in-system + not
 * cloaked + not EMP-disabled) and the race's space-structure priority
 * order; fires `buildSpaceStructure` on the first (constructor × kind)
 * pair that resolves (catalogue cost present, empire affords it, valid
 * target available). For site-gated kinds (mining / refinery), targets
 * the nearest matching environmental entity in the Constructor's
 * system. For open-space kinds (Sensor Buoy, Weapons Platforms, Repair
 * Yard, Warp Gate, Minefield), targets a point near the Constructor.
 *
 * Per-tick cap: one build dispatch across all the empire's Constructors
 * per AI-tick (matches the pre-MFix7.5a behaviour when this lived inside
 * the takeEconomyAction cascade). Multiple Constructors get parallelism
 * across consecutive AI-ticks rather than within one. Determinism
 * preserved by walking constructors in id-ascending order.
 *
 * Cloaked-Constructor edge case (RACE-CC defaultCloak): we skip cloaked
 * constructors here because `applyBuildSpaceStructure` rejects cloaked
 * ships. A future v4M0 pass can issue a setCloak: false before
 * dispatching.
 */
function dispatchConstructorBuilds(
  state: GameState,
  empire: Empire,
  config: AiRaceConfig,
): void {
  const constructorChassis = CONSTRUCTOR_CHASSIS_BY_RACE[empire.raceId];
  const constructors = [...state.ships.values()]
    .filter((s) => s.ownerEmpireId === empire.id)
    .filter((s) => s.chassis === constructorChassis)
    .filter((s) => s.constructionOrder === null)
    .filter((s) => s.systemId !== null)
    .filter((s) => !s.cloaked)
    .filter((s) => s.disabledTicks === 0)
    .sort((a, b) => a.id - b.id);
  if (constructors.length === 0) return;

  const sd = state.structuresData;
  for (const constructor of constructors) {
    for (const kind of config.spaceStructurePriority) {
      // v4M4.e.2 — race-gate. Skip kinds the empire's race cannot
      // build (maxLevelByRace[race] === 0). AI silently skips — no
      // rejection-ledger entry (only player-initiated builds toast).
      const spaceRow = sd.spaceByKind[kind];
      if (spaceRow) {
        const maxLevel = spaceRow.maxLevelByRace[empire.raceId] ?? 1;
        if (maxLevel <= 0) continue;
      }
      const cost = spaceBuildCostForKind(sd, kind);
      if (cost.credits <= 0) continue; // missing catalogue entry
      if (empire.resources.credits < cost.credits) continue;
      if (empire.resources.minerals < cost.minerals) continue;

      const target = pickBuildTarget(state, constructor, kind);
      if (target === null) continue;

      applyCommand(state, {
        kind: 'buildSpaceStructure',
        empireId: empire.id,
        constructorShipId: constructor.id,
        structureKind: kind,
        sx: target.sx,
        sy: target.sy,
      });
      return;
    }
  }
}

/**
 * Find a suitable target (sx, sy) for the Constructor's build of `kind`.
 * For site-gated kinds, picks the centre of the nearest matching
 * environmental entity in the Constructor's system. For open-space
 * kinds, picks a point close to the Constructor (slight offset so the
 * structure doesn't overlap the Constructor itself).
 */
function pickBuildTarget(
  state: GameState,
  constructor: ShipInstance,
  kind: string,
): { sx: number; sy: number } | null {
  if (constructor.systemId === null) return null;
  const required = spaceSiteRequirementForKind(state.structuresData, kind);
  if (required !== 'open') {
    let best: EnvironmentEntity | null = null;
    let bestDistSq = Infinity;
    for (const env of state.environment.values()) {
      if (env.systemId !== constructor.systemId) continue;
      if (env.kind !== required) continue;
      const dx = env.sx - constructor.sx;
      const dy = env.sy - constructor.sy;
      const d = dx * dx + dy * dy;
      if (d < bestDistSq) {
        bestDistSq = d;
        best = env;
      }
    }
    return best ? { sx: best.sx, sy: best.sy } : null;
  }
  // Open-space kind — place near the Constructor but with a small
  // offset so the built structure doesn't land exactly on top of it.
  return {
    sx: clamp01(constructor.sx + 0.03),
    sy: clamp01(constructor.sy + 0.03),
  };
}

/**
 * Fallback economy action: find the lowest-level built structure in the
 * race's structure-priority list and upgrade it. Stops after one
 * successful upgrade so the AI doesn't drain its treasury on cascading
 * upgrades in a single tick.
 */
function tryUpgradeStructure(
  state: GameState,
  empire: Empire,
  config: AiRaceConfig,
): boolean {
  const sd = state.structuresData;
  for (const kind of config.structurePriority) {
    // Per-kind cost lookup — different kinds may have different costs in the
    // post-v3M14b catalogue. Early-out per kind rather than globally.
    if (empire.resources.credits < buildCreditsForKind(sd, kind)) continue;
    if (empire.resources.minerals < buildMineralsForKind(sd, kind)) continue;
    for (const planetId of empire.planetIds) {
      const planet = state.planets.get(planetId);
      if (!planet) continue;
      for (const id of planet.structureIds) {
        const s = state.structures.get(id);
        if (!s || s.kind !== kind) continue;
        // MFix5.3 — race-gated cap (Embassy: RACE-AA L2, BB/CC L1).
        if (s.level < 1 || s.level >= maxLevelForKindByRace(sd, s.kind, empire.raceId)) continue;
        if (s.buildTicksRemaining > 0) continue;
        applyCommand(state, {
          kind: 'upgradeStructure',
          empireId: empire.id,
          structureId: s.id,
        });
        return true;
      }
    }
  }
  return false;
}

// ---------------------------------------------------------------------------
// Coloniser dispatch (v3M7.3a)
// ---------------------------------------------------------------------------

/**
 * For each idle Coloniser, if `config.coloniserPriority > 0`, find the
 * nearest landable uninhabited planet in the same system and queue a
 * `moveAndColonise` order. Landability requires either Gaia (universal)
 * or the matching `Settlement: <Biome>` tech (matches the colonise
 * command's tech gate). Cross-system colonisation is deferred to v3M7.3b
 * (inter-system jumps); this pass only operates within the Coloniser's
 * current system.
 *
 * "Idle" means: in-system, no moveOrder, no pendingColoniseTargetId, no
 * constructionOrder, not EMP-disabled. The auto-colonise hook in
 * `advance.ts` (MFix3.4) takes over once the Coloniser arrives at the
 * planet.
 *
 * `coloniserPriority` is a 0..10 knob: 0 disables dispatch entirely
 * (Fortifier / Aggressive), >=1 dispatches all idle Colonisers per
 * AI-tick. Finer-grained throttling is a v3M10 balance concern.
 */
function dispatchIdleColonisers(
  state: GameState,
  empire: Empire,
  config: AiRaceConfig,
): void {
  if (config.coloniserPriority <= 0) return;

  const colonisers = empire.shipIds
    .map((id) => state.ships.get(id))
    .filter((s): s is ShipInstance => s !== undefined)
    .filter((s) => s.systemId !== null)
    .filter((s) => s.moveOrder === null)
    .filter((s) => s.pendingColoniseTargetId === null)
    .filter((s) => s.constructionOrder === null)
    .filter((s) => s.disabledTicks === 0)
    .filter((s) => {
      const tmpl = state.shipTemplates.get(`${empire.raceId}:${s.chassis}`);
      return (
        tmpl !== undefined &&
        tmpl.specialAbilities.includes(COLONISER_SPECIAL_ABILITY)
      );
    })
    .sort((a, b) => a.id - b.id);
  if (colonisers.length === 0) return;

  // Track planets we dispatch to in this pass so two Colonisers don't
  // both target the same planet on the same tick.
  const claimed = new Set<number>();
  // MFix6.1 — Track destination systems claimed for cross-system
  // dispatch so two Colonisers don't both jump to the same neighbour
  // looking for the same target on a single tick.
  const claimedSystems = new Set<SystemId>();

  for (const ship of colonisers) {
    // Same-system fast path — if a landable planet exists in the
    // Coloniser's current system, sail-and-colonise.
    const target = pickColoniseTarget(state, empire, ship, claimed);
    if (target !== null) {
      claimed.add(target.id);
      applyCommand(state, {
        kind: 'moveAndColonise',
        empireId: empire.id,
        shipId: ship.id,
        targetPlanetId: target.id,
      });
      continue;
    }

    // MFix6.1 — Cross-system fallback. The home system has no landable
    // planet (uninhabited Gaia or matching Settlement-Biome tech).
    // Walk the jump graph (single-hop) for the nearest linked system
    // that does. Issue moveAndJump toward the JP; on the next AI tick,
    // after the Coloniser arrives in the new system, this same
    // dispatcher will pick up the same-system path and finish the
    // colonise.
    const cross = pickCrossSystemColoniseTarget(
      state,
      empire,
      ship,
      claimedSystems,
    );
    if (cross !== null) {
      claimedSystems.add(cross.targetSystemId);
      // v4M5.3b2 — dispatch the new targetSystemId schema. Internally
      // resolved to a JP via `resolveJumpPointForCommand` (commands.ts).
      applyCommand(state, {
        kind: 'moveAndJumpShips',
        shipIds: [ship.id],
        targetSystemId: cross.targetSystemId,
        arrivalAngle: null,
      });
    }
  }
}

/**
 * Compute the single-ship effective hyperdrive range for an AI ship.
 * Same formula as `applyJumpShips`'s fleet-min calculation, scoped to
 * the one ship the AI is about to dispatch. Returns 0 if the chassis
 * template can't be resolved (caller treats as "ship can't jump").
 */
function shipHyperdriveRange(
  state: GameState,
  empire: Empire,
  ship: ShipInstance,
): number {
  const tmpl = state.shipTemplates.get(`${empire.raceId}:${ship.chassis}`);
  if (!tmpl) return 0;
  return tmpl.hyperdriveRange * hyperdriveRangeMultiplier(state, empire);
}

/**
 * MFix6.1 + v4M5.5 — Cross-system dispatch helper for
 * `dispatchIdleColonisers`. Walks every system in the galaxy looking
 * for one with a landable uninhabited planet for this empire (Gaia or
 * matching Settlement: <Biome> tech); for each candidate, asks the
 * pathfinder for the first range-respecting hop from the Coloniser's
 * current system. Returns the closest reachable candidate's first
 * step, or null when no path exists.
 *
 * v4M5.5 — `firstStepTowards` returns the candidate itself when
 * it's a direct neighbour in range, or an intermediate stepping-stone
 * when multi-hop pathing is needed. The AI re-evaluates each tick, so
 * the Coloniser walks the path one hop at a time and reaches the
 * landable system across multiple ticks.
 */
function pickCrossSystemColoniseTarget(
  state: GameState,
  empire: Empire,
  ship: ShipInstance,
  claimedSystems: ReadonlySet<SystemId>,
): { targetSystemId: SystemId } | null {
  if (ship.systemId === null) return null;
  const sourceSystemId = ship.systemId;
  const src = state.systems.get(sourceSystemId);
  if (!src) return null;
  const fleetRange = shipHyperdriveRange(state, empire, ship);
  if (fleetRange <= 0) return null;

  let best: {
    firstStep: SystemId;
    targetDistSq: number;
    targetSystemId: SystemId;
  } | null = null;

  for (const candidate of state.systems.values()) {
    if (candidate.id === sourceSystemId) continue;
    if (claimedSystems.has(candidate.id)) continue;

    // Cheap landability scan over the candidate. The same-system
    // path on the next AI tick picks the actual planet after the
    // Coloniser arrives.
    let hasLandable = false;
    for (const planet of state.planets.values()) {
      if (planet.systemId !== candidate.id) continue;
      if (planet.ownerEmpireId !== null) continue;
      if (planet.currentPopulation > 0) continue;
      if (planet.biome !== 'gaia') {
        const xlsxBiome = BIOME_XLSX_LABEL[planet.biome];
        if (!empire.researchedTechs.has(`Settlement: ${xlsxBiome}`)) continue;
      }
      hasLandable = true;
      break;
    }
    if (!hasLandable) continue;

    const firstStep = firstStepTowards(
      state,
      sourceSystemId,
      candidate.id,
      fleetRange,
    );
    if (firstStep === null) continue;

    const dx = candidate.x - src.x;
    const dy = candidate.y - src.y;
    const distSq = dx * dx + dy * dy;
    if (
      best === null ||
      distSq < best.targetDistSq ||
      (distSq === best.targetDistSq && candidate.id < best.targetSystemId)
    ) {
      best = { firstStep, targetDistSq: distSq, targetSystemId: candidate.id };
    }
  }
  return best ? { targetSystemId: best.firstStep } : null;
}

/**
 * Pick the nearest landable uninhabited planet in the Coloniser's system
 * that the empire could legally colonise on arrival (Gaia or matching
 * Settlement: <Biome> tech). Tie-break by ascending planet id for
 * determinism. Returns null if no candidate exists.
 */
function pickColoniseTarget(
  state: GameState,
  empire: Empire,
  ship: ShipInstance,
  claimed: ReadonlySet<number>,
): Planet | null {
  if (ship.systemId === null) return null;
  let best: { planet: Planet; distSq: number } | null = null;
  for (const planet of state.planets.values()) {
    if (planet.systemId !== ship.systemId) continue;
    if (planet.ownerEmpireId !== null) continue;
    if (planet.currentPopulation > 0) continue;
    if (claimed.has(planet.id)) continue;
    if (planet.biome !== 'gaia') {
      const xlsxBiome = BIOME_XLSX_LABEL[planet.biome];
      if (!empire.researchedTechs.has(`Settlement: ${xlsxBiome}`)) continue;
    }
    const dx = planet.systemX - ship.sx;
    const dy = planet.systemY - ship.sy;
    const d = dx * dx + dy * dy;
    if (
      best === null ||
      d < best.distSq ||
      (d === best.distSq && planet.id < best.planet.id)
    ) {
      best = { planet, distSq: d };
    }
  }
  // Range is not gated here — moveAndColonise sets the move target to
  // the planet centre and `advancePendingColonisations` (advance.ts)
  // commits the colonise the moment the ship arrives in landing range.
  return best ? best.planet : null;
}

// ---------------------------------------------------------------------------
// Retreat (v3M7.3c)
// ---------------------------------------------------------------------------

/**
 * For each AI combat ship below `config.retreatHullThreshold`, override
 * its moveOrder with a retreat order. In the homeworld system the ship
 * heads back to the homeworld; in a foreign system it heads for the
 * nearest linked jump point (back-jump exit). Higher-quality "jump
 * specifically toward home" graph-routing is a v3M10 polish concern.
 *
 * Excluded from retreat:
 *   - Scouts — non-combat hull, jump-mission already; they keep their
 *     v3M7.3b orders.
 *   - Constructors / Colonisers — specialist intent supersedes retreat.
 *   - Ships with a pending strategic order (`pendingJumpSystemId`,
 *     `pendingColoniseTargetId`, `constructionOrder`) — already moving
 *     out of harm's way.
 *   - Mid-jump ships (`systemId === null`) — can't be ordered while
 *     in transit.
 *   - EMP-disabled ships — can't move regardless.
 *
 * Re-issuing the same retreat order each AI-tick is idempotent: the
 * move applier re-stamps the target. Cost is negligible at AI-tick
 * cadence.
 */
function executeRetreats(
  state: GameState,
  empire: Empire,
  config: AiRaceConfig,
): void {
  if (config.retreatHullThreshold <= 0) return;
  const homeworldPos = pickHomeworldPosition(state, empire);
  const constructorChassis = CONSTRUCTOR_CHASSIS_BY_RACE[empire.raceId];

  const ships = empire.shipIds
    .map((id) => state.ships.get(id))
    .filter((s): s is ShipInstance => s !== undefined)
    .filter((s) => s.systemId !== null)
    .filter((s) => s.disabledTicks === 0)
    .filter((s) => s.pendingJumpSystemId === null)
    .filter((s) => s.pendingColoniseTargetId === null)
    .filter((s) => s.constructionOrder === null)
    .filter((s) => s.chassis !== SCOUT_CHASSIS)
    .filter((s) => s.chassis !== constructorChassis)
    .sort((a, b) => a.id - b.id);

  for (const ship of ships) {
    const tmpl = state.shipTemplates.get(`${empire.raceId}:${ship.chassis}`);
    if (!tmpl) continue;
    if (tmpl.specialAbilities.includes(COLONISER_SPECIAL_ABILITY)) continue;

    const maxHull = effectiveMaxHull(state, empire, tmpl);
    if (maxHull <= 0) continue;
    const ratio = ship.currentHull / maxHull;
    if (ratio >= config.retreatHullThreshold) continue;

    if (homeworldPos !== null && ship.systemId === homeworldPos.systemId) {
      // Same system: flee toward homeworld surface position.
      applyCommand(state, {
        kind: 'moveShips',
        shipIds: [ship.id],
        targetX: homeworldPos.sx,
        targetY: homeworldPos.sy,
      });
      continue;
    }

    // Foreign system: head for the nearest in-range adjacent system
    // and bounce out. Retreat stays single-hop on purpose — a critical-
    // hull ship can't afford the multi-tick re-evaluation of a longer
    // path, and the v4M5.5 range gate skips unreachable neighbours.
    const dest = nearestAdjacentSystem(state, empire, ship);
    if (dest === null) continue;
    applyCommand(state, {
      kind: 'moveAndJumpShips',
      shipIds: [ship.id],
      targetSystemId: dest,
      arrivalAngle: null,
    });
  }
}

/**
 * Find the adjacent system whose galaxy-frame distance from the ship's
 * current system is smallest. Ties broken by ascending destination id.
 * Returns null if the ship is mid-jump or its system has no adjacencies.
 *
 * v4M5.3b3c — replaces the v3-era `nearestLinkedJumpPoint` helper.
 * Continuous Jump Area model has no JP entities; the system-pair
 * adjacency lives directly on `StarSystem.adjacentSystemIds`.
 */
function nearestAdjacentSystem(
  state: GameState,
  empire: Empire,
  ship: ShipInstance,
): SystemId | null {
  if (ship.systemId === null) return null;
  const src = state.systems.get(ship.systemId);
  if (!src) return null;
  // v4M5.5 — range gate. A retreating ship beyond its hyperdrive
  // range to every neighbour can't actually bounce out; return null
  // so the caller falls through to in-system movement (or just sits
  // dying, but that's the player's range-investment problem).
  const fleetRange = shipHyperdriveRange(state, empire, ship);
  if (fleetRange <= 0) return null;
  const fleetRangeSq = fleetRange * fleetRange;
  let best: { destId: SystemId; distSq: number } | null = null;
  for (const destId of src.adjacentSystemIds) {
    const dst = state.systems.get(destId);
    if (!dst) continue;
    const dx = dst.x - src.x;
    const dy = dst.y - src.y;
    const d = dx * dx + dy * dy;
    if (d > fleetRangeSq) continue; // beyond retreat range
    if (
      best === null ||
      d < best.distSq ||
      (d === best.distSq && destId < best.destId)
    ) {
      best = { destId, distSq: d };
    }
  }
  return best ? best.destId : null;
}

// ---------------------------------------------------------------------------
// Inter-system scout jumps (v3M7.3b)
// ---------------------------------------------------------------------------

/**
 * Scout chassis identifier. v1 each race's Class-1 recon ship is named
 * literally "Scout" in the Ships.xlsx roster — kept as a named constant
 * so future renames stay grep-able. v3M7.3b only operates on this
 * chassis; the broader fleet stays in-system.
 */
const SCOUT_CHASSIS = 'Scout';

/**
 * Dispatch idle Scouts on exploration jumps when `jumpWillingness > 0`.
 *
 * "Idle" means: chassis === 'Scout', in-system, no moveOrder, no
 * pendingJumpSystemId, no constructionOrder, not EMP-disabled. For each
 * idle Scout we pick the jump point in its system whose linked system
 * the empire has the LEAST vision of (drives exploration toward unknown
 * frontiers). Dispatch runs through `moveAndJumpShips` so the existing
 * sail-then-jump pipeline handles fuel checks at JP arrival.
 *
 * Per-tick cap (v3M7.3b first cut): one dispatch per AI-tick. Higher
 * `jumpWillingness` will scale the cap in v3M10 balance — for now the
 * knob is purely on/off (>0 dispatches, ===0 stays home, matching
 * Fortifier behaviour). Determinism: scouts walked in id-ascending
 * order; ties between candidate destinations broken by ascending
 * systemId.
 */
function dispatchScoutJumps(
  state: GameState,
  empire: Empire,
  config: AiRaceConfig,
): void {
  if (config.jumpWillingness <= 0) return;

  const scouts = empire.shipIds
    .map((id) => state.ships.get(id))
    .filter((s): s is ShipInstance => s !== undefined)
    .filter((s) => s.chassis === SCOUT_CHASSIS)
    .filter((s) => s.systemId !== null)
    .filter((s) => s.moveOrder === null)
    .filter((s) => s.pendingJumpSystemId === null)
    .filter((s) => s.constructionOrder === null)
    .filter((s) => s.disabledTicks === 0)
    .sort((a, b) => a.id - b.id);
  if (scouts.length === 0) return;

  // First cut: one dispatch per AI-tick. Picking the lowest-id idle
  // scout keeps the choice deterministic.
  const scout = scouts[0]!;
  const target = pickScoutJumpDestination(state, empire, scout);
  if (target === null) return;

  // v4M5.3b3b — helper returns just destSystemId; appliers consume it
  // natively per `HYPERSPACE.md §4`.
  applyCommand(state, {
    kind: 'moveAndJumpShips',
    shipIds: [scout.id],
    targetSystemId: target.destSystemId,
    arrivalAngle: null,
  });
}

/**
 * v4M5.5 — Walk every system in the galaxy (not just neighbours) and
 * pick the lowest-visibility-count target the scout can reach
 * (single-hop or multi-hop). Returns the FIRST-STEP system to dispatch
 * the scout to; on subsequent AI ticks the scout re-evaluates and
 * continues stepping toward the destination. Range-respecting
 * pathfinding so the scout never tries to jump beyond its single-ship
 * hyperdriveRange in one hop.
 */
function pickScoutJumpDestination(
  state: GameState,
  empire: Empire,
  scout: ShipInstance,
): { destSystemId: number } | null {
  if (scout.systemId === null) return null;
  const sourceSystemId = scout.systemId;
  const fleetRange = shipHyperdriveRange(state, empire, scout);
  if (fleetRange <= 0) return null;

  const teamMap = state.teamVision.get(empire.id);
  function visibleShipsInSystem(systemId: number): number {
    if (!teamMap) return 0;
    let n = 0;
    for (const rec of teamMap.values()) {
      if (rec.lastSeenSystemId === systemId) n++;
    }
    return n;
  }

  let best: {
    firstStep: number;
    candidateId: number;
    score: number;
  } | null = null;
  for (const candidate of state.systems.values()) {
    if (candidate.id === sourceSystemId) continue;
    const firstStep = firstStepTowards(
      state,
      sourceSystemId,
      candidate.id,
      fleetRange,
    );
    if (firstStep === null) continue;
    const score = visibleShipsInSystem(candidate.id);
    if (
      best === null ||
      score < best.score ||
      (score === best.score && candidate.id < best.candidateId)
    ) {
      best = { firstStep, candidateId: candidate.id, score };
    }
  }
  return best ? { destSystemId: best.firstStep } : null;
}

// ---------------------------------------------------------------------------
// Inter-system combat jumps (MFix7.5b)
// ---------------------------------------------------------------------------

/**
 * Planet-side structure kinds that count as "the home system has some
 * defences built" for MFix7.5b's combat-jump gate. A non-Aggressive
 * empire (Fortifier, Economist, Technologist, Expansionist) only starts
 * pushing combat ships through jump points once at least one of these
 * is up at L1+ on its homeworld — keeps turtles from naked-rushing
 * before they've established home defence, while still letting them
 * leave home eventually.
 */
const DEFENSIVE_PLANET_STRUCTURE_KINDS: ReadonlySet<StructureKind> = new Set<StructureKind>([
  'planetary-shields',
  'underground-bunkers',
  'energy-surface-battery',
  'kinetic-surface-battery',
  'orbital-defence-platform',
]);

/**
 * MFix7.5b / v4M8.3 — Dispatch one idle-fleet **task force** per AI tick
 * on an inter-system jump. v4M8.3 promoted this from "one ship per
 * tick" to "one cluster per tick" — ships in the same source system
 * form a task force and dispatch together. Per-archetype
 * `minTaskForceSize` gates the cluster (Aggressor 2, Defender 4, etc.).
 *
 * Gate (both must hold):
 *   1. `behaviour.minReadinessRatio > 1.0` triggers the home-defence
 *      check — Defender / Expander / Explorer / Diplomat / Researcher
 *      wait until the homeworld carries at least one Level 1+ defensive
 *      planet structure (`DEFENSIVE_PLANET_STRUCTURE_KINDS`). Aggressor
 *      (0.8) skips the gate and dispatches unconditionally. Replaces
 *      the pre-v4M8.3 `aggressive === true || homeworldHasDefences`
 *      gate that read from the legacy `AiRaceConfig`.
 *   2. At least one cluster of size `>= behaviour.minTaskForceSize`
 *      exists among the empire's eligible combat ships.
 *
 * Eligibility (per ship, unchanged from pre-v4M8.3):
 *   - In-system, no moveOrder, no pendingJumpSystemId, no construction,
 *     not EMP-disabled, not cloaked.
 *   - Not a Scout (own dispatch path), not a Coloniser (own path), not
 *     a Constructor (own path), not Stationary (M14 space structures /
 *     ODP ring chassis — never jump).
 *
 * Per-tick cap: one cluster per AI tick at default difficulty
 * (Easy/Medium/Hard). Best-scoring cluster across the empire's source
 * systems wins; ties broken by lowest sourceSystemId for determinism.
 * v4M8.9a — Brutal `coordinatedAssaultEnabled` flag (per AI.md §3.5 +
 * §5.3) lifts the cap: every cluster with score > 0 dispatches in the
 * same tick. Score-positive gate applies to BOTH paths uniformly —
 * the legacy code dispatched the best-scoring cluster even at
 * negative score ("worse than doing nothing"), which v4M8.9a tightens.
 *
 * Destination scoring: threat-weighted per AI.md §5.1 via
 * `pickCombatJumpDestinationThreatWeighted`. `targetScore =
 * strategicValue × strategicWeight − dangerFactor × enemyStrength /
 * ownReadiness`. `minReadinessRatio` gates engagement against
 * overpowered enemy systems. Multi-hop pathing per AI.md §5.2 — A*
 * via `firstStepTowardsWeighted` when the archetype's
 * `pathFuelWeight > 0` and the fleet's fuel/speed scalars are
 * non-zero, otherwise BFS via `firstStepTowards`.
 *
 * Fleet-aggregate scalars (per AI.md §5.3):
 *   - `fleetRange = min(hyperdriveRange × techMul)` across the cluster
 *     — slowest-ranged ship gates the dispatch.
 *   - `fuelCostPerUnit = max(hyperdriveFuelUsePerUnit × techMul)` —
 *     worst-case so the path picker doesn't strand a thirsty ship.
 *   - `travelTimePerUnit = max(1 / (hyperdriveSpeed × techMul))` —
 *     slowest ship sets fleet pace.
 *
 * Deferred to later v4M8.x checkpoints: recall on home-defence
 * emergency (v4M8.5), per-empire dispatch-claim state for cross-tick
 * dedup (v4M8.7), RACE-CC cloaked-Constructor handling (v4M8.4).
 */
function dispatchCombatJumps(
  state: GameState,
  empire: Empire,
): void {
  // v4M8.3 — consumer flip. Reads `EffectiveBehaviour` (composed from
  // archetype × difficulty × race overlay) rather than the legacy
  // `AiRaceConfig`'s `aggressive` + `jumpWillingness` fields. Per-tick
  // cap shifted from "one ship" to "one cluster" — multi-cluster
  // (Brutal coordinated assault) defers to v4M8.9.
  const behaviour = composeEffectiveBehaviour(state, empire);

  // Home-defence gate. v4M8.3 derives "wait for home defences" from
  // `behaviour.minReadinessRatio > 1.0`. Aggressor (0.8) skips the
  // gate and dispatches unconditionally; Defender (1.4) / Expander
  // (1.6) / Explorer (1.6) / Diplomat (1.4) / Researcher (1.4) wait
  // until the homeworld carries at least one defensive structure.
  // Cleaner than threading a new `requiresHomeDefences` xlsx column —
  // readiness ratio already captures the "is this archetype cautious"
  // signal.
  if (
    behaviour.minReadinessRatio > 1.0 &&
    !homeworldHasDefences(state, empire)
  ) {
    return;
  }

  // Inventory combat-ready ships. Filter chain identical to the
  // pre-v4M8.3 path — eligibility rules haven't changed, just the
  // dispatch decision built on top of them.
  const constructorChassis = CONSTRUCTOR_CHASSIS_BY_RACE[empire.raceId];
  const candidates = empire.shipIds
    .map((id) => state.ships.get(id))
    .filter((s): s is ShipInstance => s !== undefined)
    .filter((s) => s.systemId !== null)
    .filter((s) => s.moveOrder === null)
    .filter((s) => s.pendingJumpSystemId === null)
    .filter((s) => s.constructionOrder === null)
    .filter((s) => s.disabledTicks === 0)
    .filter((s) => !s.cloaked)
    .filter((s) => s.chassis !== SCOUT_CHASSIS)
    .filter((s) => s.chassis !== constructorChassis)
    .filter((s) => {
      const tmpl = state.shipTemplates.get(`${empire.raceId}:${s.chassis}`);
      if (!tmpl) return false;
      if (tmpl.specialAbilities.includes(COLONISER_SPECIAL_ABILITY)) return false;
      if (tmpl.specialAbilities.includes(STATIONARY_SPECIAL_ABILITY)) return false;
      return true;
    })
    .sort((a, b) => a.id - b.id);
  if (candidates.length === 0) return;

  // v4M8.3 — cluster by source system. Ships in the same system form
  // a task force; dispatch as a unit per AI.md §5.3.
  const clusters = new Map<SystemId, ShipInstance[]>();
  for (const ship of candidates) {
    const sysId = ship.systemId!; // non-null per filter above
    if (!clusters.has(sysId)) clusters.set(sysId, []);
    clusters.get(sysId)!.push(ship);
  }

  // For each cluster: derive fleet-aggregate scalars, call the
  // threat-weighted picker, collect every viable dispatch into a
  // ranked list. Default dispatch cadence = top cluster only
  // (matches the legacy "one ship per tick" cadence — clusters of
  // size N still consume one dispatch slot). v4M8.9a Brutal
  // coordinated assault (`behaviour.coordinatedAssaultEnabled`,
  // per AI.md §3.5 + §5.3) lifts the cap and dispatches every
  // positive-score cluster in the same tick — Brutal AI attacks
  // multiple fronts at once.
  const viableDispatches: Array<{
    sourceSystemId: SystemId;
    fleet: ShipInstance[];
    firstStep: SystemId;
    score: number;
  }> = [];

  for (const [sourceSystemId, fleet] of clusters) {
    // `minTaskForceSize` gate per AI.md §5.3. Aggressor 2 / Defender 4
    // / Expander 5 / Explorer 5 / Diplomat 4 / Researcher 4 (xlsx-
    // tunable per v4M8.1's Archetypes tab).
    if (fleet.length < behaviour.minTaskForceSize) continue;

    // Fleet-aggregate scalars per AI.md §5.2:
    //   - fleetRange = min hyperdriveRange × techMul (slowest-ranged
    //     ship gates the cluster)
    //   - fuelCostPerUnit = max hyperdriveFuelUsePerUnit × techMul
    //     (worst-case so the path picker doesn't strand a thirsty ship)
    //   - travelTimePerUnit = max 1 / (hyperdriveSpeed × techMul)
    //     (slowest ship sets fleet pace)
    let fleetRange = Infinity;
    let fuelCostPerUnit = 0;
    let travelTimePerUnit = 0;
    const rangeMul = hyperdriveRangeMultiplier(state, empire);
    const costMul = hyperdriveCostMultiplier(state, empire);
    const speedMul = hyperdriveSpeedMultiplier(state, empire);
    for (const ship of fleet) {
      const tmpl = state.shipTemplates.get(`${empire.raceId}:${ship.chassis}`);
      if (!tmpl) continue;
      const range = tmpl.hyperdriveRange * rangeMul;
      if (range < fleetRange) fleetRange = range;
      const fuel = tmpl.hyperdriveFuelUsePerUnit * costMul;
      if (fuel > fuelCostPerUnit) fuelCostPerUnit = fuel;
      const speed = tmpl.hyperdriveSpeed * speedMul;
      const timePerUnit = speed > 0 ? 1 / speed : 0;
      if (timePerUnit > travelTimePerUnit) travelTimePerUnit = timePerUnit;
    }
    if (!Number.isFinite(fleetRange) || fleetRange <= 0) continue;

    const target = pickCombatJumpDestinationThreatWeighted(
      state,
      empire,
      sourceSystemId,
      fleet,
      behaviour,
      fleetRange,
      fuelCostPerUnit,
      travelTimePerUnit,
    );
    if (target === null) continue;
    // v4M8.9a — collect every viable dispatch. Brutal coordinated
    // assault dispatches them all; default cadence picks just the
    // top one. Filter to score > 0 so we don't issue "worse than
    // doing nothing" dispatches even at Brutal — that's a sim
    // anti-feature, not an aggression knob.
    if (target.score <= 0) continue;
    viableDispatches.push({
      sourceSystemId,
      fleet,
      firstStep: target.firstStep,
      score: target.score,
    });
  }

  if (viableDispatches.length === 0) return;

  // Rank descending by score; tie-break on ascending sourceSystemId
  // for deterministic dispatch order (claim emission ordering and
  // alert-feed ordering both depend on this).
  viableDispatches.sort((a, b) => {
    if (a.score !== b.score) return b.score - a.score;
    return a.sourceSystemId - b.sourceSystemId;
  });

  // v4M8.9a — Brutal coordinated assault per AI.md §3.5 + §5.3.
  // When the Brutal `coordinatedAssaultEnabled` flag is set, dispatch
  // every cluster in the same tick. Otherwise dispatch just the
  // top-scoring cluster (the legacy v4M8.3 cadence). Claim emission +
  // Brutal-Phantom pre-cloak fire per dispatched fleet, so
  // multi-cluster runs naturally generate multi-claim and multi-cloak
  // side-effects.
  const dispatches = behaviour.coordinatedAssaultEnabled
    ? viableDispatches
    : [viableDispatches[0]!];

  const cloakDrain = state.balanceData.detection.cloakEnergyDrainPerTick;
  const brutalPhantom =
    empire.aiDifficulty === 'Brutal' &&
    empire.raceId === 'RACE-CC' &&
    empire.researchedTechs.has(CLOAKING_TECH_ID);

  for (const dispatch of dispatches) {
    // Dispatch the whole cluster together. `moveAndJumpShips`
    // handles the sail-then-jump pipeline, fuel checks, charge-up
    // sequencing.
    applyCommand(state, {
      kind: 'moveAndJumpShips',
      shipIds: dispatch.fleet.map((s) => s.id),
      targetSystemId: dispatch.firstStep,
      arrivalAngle: null,
    });

    // v4M8.7b — Team-aware coordinated combat dispatch claim
    // emission. Push a claim on the destination system so other
    // teammates' pickers see this team is already committed and
    // spread their attention elsewhere. Per-hop granularity — a
    // multi-hop fleet re-claims at the next dispatch tick after
    // each hop, which matches v4M8.3's pick-after-each-hop
    // dispatch cadence. No-op for solo empires (teamId === null)
    // — see pushTeamClaim. Under v4M8.9a Brutal coordinated assault
    // this fires once per dispatched fleet — multi-fleet runs
    // generate multiple claims for the same team in the same tick.
    pushTeamClaim(state, empire, dispatch.firstStep);

    // v4M8.4d — Brutal Phantom coordinated cloaked-fleet ambush
    // per AI.md §5.4. Standard Phantom (non-Brutal) only cloaks on
    // arrival in foreign territory; Brutal Phantom hides the
    // charge-up too so the source-system defender never sees the
    // fleet powering up. Combined with Phantom Advanced Cloaking
    // (combatEngageUncloak=false), the task force stays invisible
    // all the way through the engagement. Maximises surprise per
    // the design lock.
    //
    // Gates mirror `applyToggleCloak`: Cloaking tech researched,
    // canCloak template flag, alive + not EMP'd, enough energy to
    // survive one drain tick. The charging-up early-out in
    // `decideShipCloakAction` (v4M8.4d helper edit) prevents the
    // next `dispatchCloakDecisions` pass from undoing the
    // pre-cloak.
    if (brutalPhantom) {
      for (const ship of dispatch.fleet) {
        const tmpl = state.shipTemplates.get(
          `${empire.raceId}:${ship.chassis}`,
        );
        if (!tmpl?.canCloak) continue;
        if (ship.cloaked) continue;
        if (ship.disabledTicks > 0) continue;
        if (ship.currentEnergy < cloakDrain) continue;
        ship.cloaked = true;
        // Mirror `applyToggleCloak`'s abort-side-effects for
        // consistency with the player-side applier semantics.
        ship.constructionOrder = null;
        ship.bombardOrder = null;
      }
    }
  }
}

/**
 * v4M8.4b — per-tick cloak dispatch for an AI empire. Calls
 * `decideShipCloakAction` for every cloakable ship the empire owns
 * and applies the result by directly mutating `ship.cloaked`,
 * mirroring `applyToggleCloak` in `commands.ts` without the
 * player-rejection-toast path (AI never gets rejection toasts).
 *
 * Gates per `commands.ts:applyToggleCloak`:
 *   1. Empire must have researched the Cloaking tech.
 *   2. Ship can't be mid-jump or EMP-disabled (helper already
 *      handles the mid-jump case; we gate disabled here).
 *   3. Cloaking requires enough current energy to survive one drain
 *      tick — without that the AI would cloak and immediately
 *      auto-uncloak in `advance-cloak.ts` next tick.
 *
 * Cloak abort side-effects (`constructionOrder = null`,
 * `bombardOrder = null`) match the player-side applier. The helper's
 * Constructor branch already returns `uncloak` when
 * `constructionOrder` is non-null, so the cloak path here should
 * never fire on a building Constructor — the defensive clear stays
 * in case a future helper change loosens that.
 */
function dispatchCloakDecisions(state: GameState, empire: Empire): void {
  if (!empire.researchedTechs.has(CLOAKING_TECH_ID)) return;
  const behaviour = composeEffectiveBehaviour(state, empire);
  const cloakDrain = state.balanceData.detection.cloakEnergyDrainPerTick;
  for (const shipId of empire.shipIds) {
    const ship = state.ships.get(shipId);
    if (!ship) continue;
    if (ship.currentHull <= 0) continue;
    if (ship.disabledTicks > 0) continue;
    const action = decideShipCloakAction(state, ship, empire, behaviour);
    if (action === 'noop') continue;
    if (action === 'cloak') {
      if (ship.cloaked) continue;
      if (ship.currentEnergy < cloakDrain) continue;
      ship.cloaked = true;
      // Mirror applyToggleCloak's abort-side-effects (defensive — the
      // helper avoids this path when constructionOrder is set, but
      // stay consistent with the player-side applier semantics).
      ship.constructionOrder = null;
      ship.bombardOrder = null;
    } else {
      // action === 'uncloak'
      if (!ship.cloaked) continue;
      ship.cloaked = false;
    }
  }
}

/**
 * v4M8.5b — home-defence recall dispatch. Pulls foreign-deployed
 * combat ships back to the empire's homeworld when
 * `detectHomeDefenceThreat` reports a critical or warning tier per
 * `docs/AI.md §5.5`. Ordering: damaged ships first (hull% ascending,
 * id tiebreak) so the most-vulnerable units are recalled to safety
 * preferentially. Recalled ships group by source system; one
 * `moveAndJumpShips` command dispatches each group (the moveAndJump
 * applier handles the sail-to-jump-line + charge-up sequencing per
 * the v4M5 pipeline).
 *
 * Out of scope (deferred to v4M8.8 / future):
 *   - Standing-orders flip on arrival home (recalled ships should
 *     default to Guard at homeworld per AI.md §5.10; for now they
 *     simply arrive home idle, which is acceptable defensive
 *     behaviour).
 *   - Team-aware recall on teammate's home being threatened (per
 *     AI.md §5 explicit out-of-scope items).
 *   - Mid-jump ship recall (no abort-jump sim primitive exists —
 *     these ships skip the recall and continue to their current
 *     destination).
 */
function dispatchHomeDefenceRecall(state: GameState, empire: Empire): void {
  const threat = detectHomeDefenceThreat(state, empire);
  if (threat.tier === 'none') return;
  if (threat.homeworldSystemId === null) return;

  const behaviour = composeEffectiveBehaviour(state, empire);

  // 'warning' tier: only Brutal empires (preEmptiveRecallEnabled)
  // react. Non-Brutal empires wait for the actual breach (critical).
  if (threat.tier === 'warning' && !behaviour.preEmptiveRecallEnabled) return;

  // Walk empire combat ships in foreign systems (anything not the
  // homeworld). Filter out mid-jump (systemId === null), dead,
  // EMP-disabled, and non-combat chassis.
  const homeworldId = threat.homeworldSystemId;
  const foreignCombat: ShipInstance[] = [];
  for (const shipId of empire.shipIds) {
    const ship = state.ships.get(shipId);
    if (!ship) continue;
    if (ship.currentHull <= 0) continue;
    if (ship.disabledTicks > 0) continue;
    if (ship.systemId === null) continue;
    if (ship.systemId === homeworldId) continue;
    if (!isCombatChassisForRecall(ship.chassis)) continue;
    foreignCombat.push(ship);
  }
  if (foreignCombat.length === 0) return;

  // Damaged first → least-able-to-fight-elsewhere recalled
  // preferentially. Tie-break on id for determinism (multiplayer
  // determinism contract — same state must dispatch the same recall
  // across hosts).
  foreignCombat.sort((a, b) => {
    if (a.currentHull !== b.currentHull) return a.currentHull - b.currentHull;
    return a.id - b.id;
  });

  // Critical: recall ceil(N × recallFraction). Warning + Brutal:
  // recall exactly warningRecallSize ships (clamped to available
  // count). Both bottom-out at 1 ship recalled when a recall fires
  // at all — otherwise the AI does nothing in a critical-threat
  // moment, which would be worse than under-reacting.
  let recallCount: number;
  if (threat.tier === 'critical') {
    recallCount = Math.max(1, Math.ceil(foreignCombat.length * behaviour.recallFraction));
  } else {
    recallCount = behaviour.warningRecallSize;
  }
  recallCount = Math.min(recallCount, foreignCombat.length);
  if (recallCount <= 0) return;

  // Group by source system. moveAndJumpShips wants a single source
  // per command (per the v4M5 pipeline's source-system invariants);
  // one command per source keeps the dispatch contract clean and
  // avoids the v4M5.2a "multi-source rejection" path.
  const recallShips = foreignCombat.slice(0, recallCount);
  const bySource = new Map<SystemId, ShipInstance['id'][]>();
  for (const ship of recallShips) {
    const sys = ship.systemId;
    if (sys === null) continue; // belt-and-suspenders — already filtered above
    const arr = bySource.get(sys);
    if (arr) {
      arr.push(ship.id);
    } else {
      bySource.set(sys, [ship.id]);
    }
  }

  for (const shipIds of bySource.values()) {
    applyCommand(state, {
      kind: 'moveAndJumpShips',
      shipIds,
      targetSystemId: homeworldId,
      arrivalAngle: null,
    });
  }
}

/**
 * v4M8.6b — AI Warp Gate auto-connect dispatch. Picks the highest-
 * scored gate-build candidate pair (via `pickBestGateCandidatePair`),
 * gates on (a) resource surplus, (b) Constructor availability in
 * each pair-system, (c) neither system already carrying a Warp
 * Gate, then issues a `buildSpaceStructure` command for each end.
 *
 * Resource gate per `AI.md §5.6`: `empire.minerals + empire.fuel >
 * 2 × gateBuildCost.minerals`. Warp Gates cost minerals (no fuel) to
 * build; the fuel half of the surplus is a buffer the AI keeps for
 * the eventual `connectWarpGate` command (v4M8.6c).
 *
 * Per-tick at most ONE gate-pair build starts (a Constructor pair
 * commits to the highest-scored pair this tick; lower-scored pairs
 * wait for the next AI tick). Avoids the AI dumping all its
 * Constructors into multiple parallel gate projects in a single
 * tick. The connection follow-up (v4M8.6c) is handled by a
 * separate dispatch step.
 *
 * Mid-build or already-built gates: the system-already-has-gate
 * check catches built gates. Mid-build gates are filtered indirectly
 * via the `constructionOrder === null` filter on the Constructor
 * pool (a Constructor mid-build can't be picked again until it
 * completes).
 */
function dispatchAiGateBuilds(state: GameState, empire: Empire): void {
  const behaviour = composeEffectiveBehaviour(state, empire);
  const pick = pickBestGateCandidatePair(state, empire, behaviour);
  if (pick === null) return;

  // Skip the build if either system already has a Warp Gate
  // (built or under construction). Avoids dispatching a duplicate
  // gate into a system that already has one.
  if (systemHasWarpGate(state, empire.id, pick.aId)) return;
  if (systemHasWarpGate(state, empire.id, pick.bId)) return;

  const sd = state.structuresData;
  const gateCost = spaceBuildCostForKind(sd, WARP_GATE_CHASSIS);
  if (gateCost.minerals <= 0) return; // missing catalogue entry — defensive

  // Resource gate: surplus minerals + fuel > 2 × single-gate
  // minerals cost. Lax enough to fire early-game once the empire
  // has Constructors + some economy; tight enough that broke
  // empires don't waste their last credits on speculative gates.
  if (
    empire.resources.minerals + empire.resources.fuel <
    2 * gateCost.minerals
  ) {
    return;
  }
  // Also gate on minerals alone — a gate costs minerals, not fuel,
  // so an empire with fuel-but-not-minerals can't actually build.
  // The applier would reject; we skip cleanly.
  if (empire.resources.minerals < gateCost.minerals) return;
  // Credits gate too — Warp Gate has a credits cost at v4M5.4 (15k).
  // Without this the dispatch would issue the command + the applier
  // would reject + push a rejection. Cheaper to filter here.
  if (empire.resources.credits < gateCost.credits) return;

  const constructorA = findIdleConstructorInSystem(state, empire, pick.aId);
  const constructorB = findIdleConstructorInSystem(state, empire, pick.bId);
  if (constructorA === null || constructorB === null) return;
  if (constructorA.id === constructorB.id) return; // same Constructor can't build both

  const targetA = pickBuildTarget(state, constructorA, WARP_GATE_CHASSIS);
  const targetB = pickBuildTarget(state, constructorB, WARP_GATE_CHASSIS);
  if (targetA === null || targetB === null) return;

  applyCommand(state, {
    kind: 'buildSpaceStructure',
    empireId: empire.id,
    constructorShipId: constructorA.id,
    structureKind: WARP_GATE_CHASSIS,
    sx: targetA.sx,
    sy: targetA.sy,
  });
  applyCommand(state, {
    kind: 'buildSpaceStructure',
    empireId: empire.id,
    constructorShipId: constructorB.id,
    structureKind: WARP_GATE_CHASSIS,
    sx: targetB.sx,
    sy: targetB.sy,
  });
}

/**
 * Returns true when `empireId` already has at least one live Warp
 * Gate ship in `systemId`. Used by `dispatchAiGateBuilds` to skip
 * dispatching a redundant gate into a system that already has one.
 */
function systemHasWarpGate(
  state: GameState,
  empireId: EmpireId,
  systemId: SystemId,
): boolean {
  for (const ship of state.ships.values()) {
    if (ship.systemId !== systemId) continue;
    if (ship.ownerEmpireId !== empireId) continue;
    if (ship.chassis !== WARP_GATE_CHASSIS) continue;
    if (ship.currentHull <= 0) continue;
    return true;
  }
  return false;
}

/**
 * Finds an idle Constructor for `empire` currently positioned in
 * `systemId`. Same filter set as `dispatchConstructorBuilds` (own,
 * race-correct Constructor chassis, no in-flight constructionOrder,
 * in-system, not cloaked, not EMP-disabled). Lowest-id wins for
 * determinism. Returns null when no candidate exists.
 */
function findIdleConstructorInSystem(
  state: GameState,
  empire: Empire,
  systemId: SystemId,
): ShipInstance | null {
  const constructorChassis = CONSTRUCTOR_CHASSIS_BY_RACE[empire.raceId];
  if (!constructorChassis) return null;
  let best: ShipInstance | null = null;
  for (const ship of state.ships.values()) {
    if (ship.ownerEmpireId !== empire.id) continue;
    if (ship.chassis !== constructorChassis) continue;
    if (ship.systemId !== systemId) continue;
    if (ship.constructionOrder !== null) continue;
    if (ship.cloaked) continue;
    if (ship.disabledTicks > 0) continue;
    if (best === null || ship.id < best.id) best = ship;
  }
  return best;
}

/**
 * v4M8.6c — AI Warp Gate connection dispatch. Walks the empire's
 * unlinked live Warp Gate ships, finds the highest-scored
 * cross-system pair via the same `pairScoreForGateCandidate`
 * helper that drove the build picker (.6a), and issues a
 * `connectWarpGate` command. Per-tick at most ONE connection
 * starts.
 *
 * Per-tick re-derivation (no pendingGatePairs state-shape addition):
 * each tick the dispatch sees the current set of empire gates +
 * warpGateLinks and decides afresh. Destroyed-gate / disconnected-
 * link / newly-built scenarios all flow through the same path.
 *
 * Resource gate: `connectWarpGate` costs minerals + fuel per
 * `warpGateConnectionCost*`. The dispatch skips when the empire
 * can't pay — same filter shape as the applier itself, just
 * pre-checked here to avoid generating a rejection.
 */
function dispatchAiGateConnections(state: GameState, empire: Empire): void {
  // Linked-gate set: any gate already in a live OR building link
  // is ineligible for a new connection per v3M18 cardinality (one
  // link per gate).
  const linkedGateIds = new Set<ShipInstance['id']>();
  for (const link of state.warpGateLinks.values()) {
    linkedGateIds.add(link.gateAId);
    linkedGateIds.add(link.gateBId);
  }

  // Empire's unlinked operational Warp Gate ships.
  const gates: ShipInstance[] = [];
  for (const ship of state.ships.values()) {
    if (ship.ownerEmpireId !== empire.id) continue;
    if (ship.chassis !== WARP_GATE_CHASSIS) continue;
    if (ship.currentHull <= 0) continue;
    if (ship.systemId === null) continue; // mid-jump (stationary in v1, defensive)
    if (linkedGateIds.has(ship.id)) continue;
    gates.push(ship);
  }
  if (gates.length < 2) return;

  // Resource gate. Match the applier's checks so we don't generate
  // a rejection when we know it would be rejected.
  const sd = state.structuresData;
  const mineralCost = warpGateConnectionCostMinerals(sd);
  const fuelCost = warpGateConnectionCostFuel(sd);
  if (empire.resources.minerals < mineralCost) return;
  if (empire.resources.fuel < fuelCost) return;

  // Pick the highest-scoring cross-system pair. Reuses the v4M8.6a
  // `pairScoreForGateCandidate` so the connection priority matches
  // the build priority — a high-value pair built earlier stays the
  // high-value pair to link first.
  const behaviour = composeEffectiveBehaviour(state, empire);
  let bestPair: { gateA: ShipInstance; gateB: ShipInstance; score: number } | null = null;
  for (let i = 0; i < gates.length; i++) {
    for (let j = i + 1; j < gates.length; j++) {
      const gateA = gates[i]!;
      const gateB = gates[j]!;
      // Same-system pair is rejected by the applier (v3M18 spec);
      // skip cleanly.
      if (gateA.systemId === gateB.systemId) continue;
      const score = pairScoreForGateCandidate(
        state,
        empire,
        behaviour,
        gateA.systemId!,
        gateB.systemId!,
      );
      if (score <= 0) continue;
      if (bestPair === null || score > bestPair.score) {
        bestPair = { gateA, gateB, score };
      }
    }
  }
  if (bestPair === null) return;

  applyCommand(state, {
    kind: 'connectWarpGate',
    empireId: empire.id,
    gateAId: bestPair.gateA.id,
    gateBId: bestPair.gateB.id,
  });
}

/**
 * Combat chassis classifier for the recall pass. Mirrors the cohort
 * in `advance-ai-cloaking.ts:classifyChassisForCloak` but inlined
 * here so the recall dispatch doesn't take a dependency on the cloak
 * helper. Carrier squadrons (synthetic chassis names ending in
 * "Squadron") count as combat for recall purposes.
 */
function isCombatChassisForRecall(chassis: string): boolean {
  if (/Squadron$/i.test(chassis)) return true;
  return /^(Corvette|Fighter|Infiltrator|Cruiser|Praetorian|Warship|Destroyer|Battleship|Dreadnought|Colossus|Carrier)$/i.test(
    chassis,
  );
}

/**
 * Returns true when the empire's first owned planet (the homeworld for
 * the purposes of `pickHomeworldPosition` and the rest of advance-ai)
 * carries at least one fully-built (level >= 1) defensive structure.
 * Used by `dispatchCombatJumps` to gate non-Aggressive empires.
 */
function homeworldHasDefences(state: GameState, empire: Empire): boolean {
  const homeworldId = empire.planetIds[0];
  if (homeworldId === undefined) return false;
  const homeworld = state.planets.get(homeworldId);
  if (!homeworld) return false;
  for (const id of homeworld.structureIds) {
    const s = state.structures.get(id);
    if (!s) continue;
    if (s.level < 1) continue;
    if (DEFENSIVE_PLANET_STRUCTURE_KINDS.has(s.kind)) return true;
  }
  return false;
}

/**
 * v4M8.8b — Apply per-archetype standing orders to ships that no
 * explicit dispatch claimed this tick. Per `AI.md §5.10`: the
 * archetype StandingOrders row carries default per-ship-class
 * orders (Aggressor scouts → "explore", Defender capitals →
 * "guard-homeworld", etc); ships that drop through every other
 * dispatch pass with `commandKind === 'idle'` pick up the fallback
 * from this pass.
 *
 * Scope for v4M8.8b: handles `'explore'`, `'guard-*'`, and `'idle'`
 * standing orders. The `'patrol-*'` variants need in-system waypoint
 * placement (setShipPatrol no-ops without waypoints) — deferred to
 * a v4M8.8 follow-up because picking sensible patrol coordinates
 * per semantic variant (`patrol-enemy-border` / `patrol-border` /
 * `patrol-diplomatic`) is non-trivial. Until then, idle small-combat
 * ships with patrol-* standing orders continue to fall through to
 * `issueMovementOrders`'s random homeworld patrol.
 *
 * Guard targets resolve to the empire's homeworld planet (first
 * owned planet, same convention as `pickHomeworldPosition`). All
 * `guard-*` variants currently route to the homeworld; specialised
 * targets (`guard-economic-hub` / `guard-research-hub` resolved
 * to the highest-economic-value / highest-research-output planet)
 * are also deferred to the follow-up.
 *
 * Filter chain: ship must be (a) `commandKind === 'idle'` (ships
 * already on patrol/guard/explore aren't re-ordered), (b) in-system
 * (`systemId !== null`), (c) not EMP-disabled, (d) not cloaked
 * (avoid wasting cloak energy on order churn), (e) not mid-charge-up
 * (don't override a pending jump). Walk in id-ascending order for
 * determinism.
 */
function applyStandingOrders(state: GameState, empire: Empire): void {
  const behaviour = composeEffectiveBehaviour(state, empire);
  const homeworldPlanetId = empire.planetIds[0];
  // No homeworld → no guard target → skip the guard-* path entirely.
  // Explore standing orders still apply without a homeworld.
  const homeworldPlanet =
    homeworldPlanetId !== undefined
      ? state.planets.get(homeworldPlanetId)
      : undefined;

  const ships = empire.shipIds
    .map((id) => state.ships.get(id))
    .filter((s): s is ShipInstance => s !== undefined)
    .filter((s) => s.commandKind === 'idle')
    .filter((s) => s.systemId !== null)
    .filter((s) => s.disabledTicks === 0)
    .filter((s) => !s.cloaked)
    .filter((s) => s.chargeUpTicksRemaining === 0)
    .sort((a, b) => a.id - b.id);

  for (const ship of ships) {
    const tmpl = state.shipTemplates.get(`${empire.raceId}:${ship.chassis}`);
    if (!tmpl) continue;
    const order = resolveStandingOrderForShip(behaviour, ship, tmpl);
    if (order === null) continue;
    if (order === 'idle') continue; // explicit no-op
    if (order === 'explore') {
      applyCommand(state, {
        kind: 'setShipExplore',
        empireId: empire.id,
        shipId: ship.id,
      });
      continue;
    }
    if (order.startsWith('guard-')) {
      // v4M8.8b scope — all guard-* variants route to the homeworld
      // planet. Specialised targets (economic-hub / research-hub)
      // deferred to the follow-up.
      if (!homeworldPlanet) continue;
      // Only issue when the ship is in the same system as the
      // homeworld — setShipGuard rejects cross-system targets at
      // applier validation time.
      if (ship.systemId !== homeworldPlanet.systemId) continue;
      applyCommand(state, {
        kind: 'setShipGuard',
        empireId: empire.id,
        shipId: ship.id,
        targetKind: 'planet',
        targetId: homeworldPlanet.id,
      });
      continue;
    }
    // patrol-* + any other unrecognised order: deferred, falls
    // through. issueMovementOrders below will give the ship a
    // random homeworld patrol step if it's in the home system.
  }
}

// v4M8.3 — `pickCombatJumpDestination` (legacy v4M5.5 single-ship,
// enemy-count-scored picker) retired. Combat-jump dispatch now flows
// through `pickCombatJumpDestinationThreatWeighted` in
// `advance-ai-targeting.ts`, called from the rewritten
// `dispatchCombatJumps` above. The new picker reads `EffectiveBehaviour`
// for per-archetype scoring weights, applies the AI.md §5.1 threat-
// weighted formula, and supports A* multi-hop pathing via the optional
// `firstStepTowardsWeighted` route (engaged when the archetype's
// `pathFuelWeight > 0` and the fleet's fuel/speed scalars are non-zero).
//
// `shipHyperdriveRange` (the single-ship range helper) survives — it's
// still used by `dispatchScoutJumps` + `dispatchIdleColonisers` for
// their cross-system pickers. v4M8.x may revisit those for fleet-style
// dispatch separately.

// ---------------------------------------------------------------------------
// Movement: orders for idle ships every AI-tick
// ---------------------------------------------------------------------------

/**
 * For every idle AI ship, issue an appropriate move order. Idle =
 * in-system, no moveOrder, no active construction, no active jump, not
 * EMP-disabled. Three paths:
 *
 *   - Constructors: nothing (Constructor build command sets the order).
 *   - Aggressive races with visible enemies in same system: close with
 *     nearest visible enemy target.
 *   - Otherwise: random patrol point within AI_PATROL_RADIUS of the
 *     empire's homeworld (first owned planet).
 */
function issueMovementOrders(
  state: GameState,
  empire: Empire,
  config: AiRaceConfig,
): void {
  const constructorChassis = CONSTRUCTOR_CHASSIS_BY_RACE[empire.raceId];
  const homeworldPos = pickHomeworldPosition(state, empire);
  const ships = empire.shipIds
    .map((id) => state.ships.get(id))
    .filter((s): s is ShipInstance => s !== undefined)
    .filter((s) => s.systemId !== null)
    .filter((s) => s.moveOrder === null)
    .filter((s) => s.constructionOrder === null)
    .filter((s) => s.disabledTicks === 0)
    .sort((a, b) => a.id - b.id);

  for (const ship of ships) {
    // Constructors handled by the Constructor-build economy step.
    if (ship.chassis === constructorChassis) continue;
    // Colonisers handled by dispatchIdleColonisers (v3M7.3a) — they
    // shouldn't patrol or chase enemies. If a Coloniser is idle here it
    // means dispatch already passed without finding a target this tick.
    const tmpl = state.shipTemplates.get(`${empire.raceId}:${ship.chassis}`);
    if (tmpl?.specialAbilities.includes(COLONISER_SPECIAL_ABILITY)) continue;

    let target: { sx: number; sy: number } | null = null;
    if (config.aggressive) {
      target = findNearestVisibleEnemyPosition(state, empire, ship);
    }
    if (target === null && homeworldPos && ship.systemId === homeworldPos.systemId) {
      target = randomPatrolPoint(state, homeworldPos.sx, homeworldPos.sy);
    }
    if (target === null) continue;

    applyCommand(state, {
      kind: 'moveShips',
      shipIds: [ship.id],
      targetX: target.sx,
      targetY: target.sy,
    });
  }
}

/**
 * First owned planet's (systemId, systemX, systemY) — serves as the AI
 * empire's "home" for patrol + retreat. Null if the empire somehow has
 * no planets (e.g. mid-conquest, once conquest lands).
 */
function pickHomeworldPosition(
  state: GameState,
  empire: Empire,
): { systemId: SystemId; sx: number; sy: number } | null {
  for (const planetId of empire.planetIds) {
    const planet = state.planets.get(planetId);
    if (!planet) continue;
    return { systemId: planet.systemId, sx: planet.systemX, sy: planet.systemY };
  }
  return null;
}

function randomPatrolPoint(
  state: GameState,
  cx: number,
  cy: number,
): { sx: number; sy: number } {
  // Use the shared RNG so patrol positions are deterministic under seed.
  const a = nextFloat(state.rng) * Math.PI * 2;
  const r = nextFloat(state.rng) * state.balanceData.ai.patrolRadius;
  return {
    sx: clamp01(cx + Math.cos(a) * r),
    sy: clamp01(cy + Math.sin(a) * r),
  };
}

/**
 * Look up the empire's team-vision map, find the nearest enemy ship in
 * the same system as `ship`, return its position. Returns null if no
 * visible enemy. Respects teamVision's `seenVia` channel — ghost
 * records (seenVia null) don't count as targets to chase (same rule as
 * pickTarget in combat).
 */
function findNearestVisibleEnemyPosition(
  state: GameState,
  empire: Empire,
  ship: ShipInstance,
): { sx: number; sy: number } | null {
  if (ship.systemId === null) return null;
  const teamMap = state.teamVision.get(empire.id);
  if (!teamMap) return null;
  let best: { sx: number; sy: number; distSq: number } | null = null;
  for (const rec of teamMap.values()) {
    if (rec.seenVia === null) continue;
    if (rec.lastSeenSystemId !== ship.systemId) continue;
    const target = state.ships.get(rec.shipId);
    if (!target || target.currentHull <= 0 || target.systemId === null) continue;
    const dx = target.sx - ship.sx;
    const dy = target.sy - ship.sy;
    const d = dx * dx + dy * dy;
    if (best === null || d < best.distSq) {
      best = { sx: target.sx, sy: target.sy, distSq: d };
    }
  }
  return best ? { sx: best.sx, sy: best.sy } : null;
}

function clamp01(n: number): number {
  if (n < 0) return 0;
  if (n > 1) return 1;
  return n;
}
